import React from 'react'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { Pressable, Text, TouchableOpacity, View, type AccessibilityActionEvent } from 'react-native'
import { AppModal } from '../ui/AppModal'
import type { TutorialHelpCopy } from '../../lib/tutorialHelp'

/** Rule on the side facing away from what the strip is attached to: below
 *  a landscape column header, or above the portrait map controls bar. */
export type TutorialStripEdge = 'top' | 'bottom'
const tutorialCardClass = (edge: TutorialStripEdge) =>
  `w-full flex-row items-center gap-2 ${edge === 'top' ? 'border-t' : 'border-b'} border-olive bg-canvas px-3 py-2`

/** Tip text block height: a 14px title, 2px gap and two 16px body lines. */
export const TIP_TEXT_MIN_HEIGHT = 14 + 2 + 16 * 2

export function TutorialHelpStrip({
  copy,
  onDisable,
  edge = 'bottom',
  collapsed = false,
  onToggleCollapsed,
}: {
  copy: TutorialHelpCopy
  /** Turn tips off — offered from the long-press popup (no inline button, so
   *  the tip text gets the room). */
  onDisable: () => void
  edge?: TutorialStripEdge
  /** Minimized to a one-line row (lightbulb + title); tap it to expand. */
  collapsed?: boolean
  onToggleCollapsed?: () => void
}) {
  const [optionsOpen, setOptionsOpen] = React.useState(false)
  const openOptions = () => setOptionsOpen(true)
  // Screen readers can't long-press reliably: expose it as an action too.
  const a11yActions = [{ name: 'longpress', label: 'Turn off tips' }]
  const onA11yAction = (event: AccessibilityActionEvent) => {
    if (event.nativeEvent.actionName === 'longpress') openOptions()
  }
  const options = (
    <TutorialTipOptions
      visible={optionsOpen}
      onClose={() => setOptionsOpen(false)}
      onDisable={() => {
        setOptionsOpen(false)
        onDisable()
      }}
    />
  )
  if (collapsed && onToggleCollapsed) {
    return (
      <>
        <TouchableOpacity
          onPress={onToggleCollapsed}
          onLongPress={openOptions}
          accessibilityRole="button"
          accessibilityLabel={`Tutorial help: ${copy.title}. Show tip`}
          accessibilityHint="Long press for tip options"
          accessibilityActions={a11yActions}
          onAccessibilityAction={onA11yAction}
          testID="tutorial-help-expand"
          className={`w-full flex-row items-center gap-2 ${edge === 'top' ? 'border-t' : 'border-b'} border-olive bg-canvas px-3 py-1`}
        >
          <MaterialCommunityIcons name="lightbulb-outline" size={16} color="#991b1b" />
          <Text className="flex-1 font-stencil text-[11px] uppercase text-blood" numberOfLines={1}>{copy.title}</Text>
          <MaterialCommunityIcons name="chevron-up" size={18} color="#991b1b" />
        </TouchableOpacity>
        {options}
      </>
    )
  }
  return (
    <>
      <Pressable
        onLongPress={openOptions}
        delayLongPress={400}
        testID="tutorial-help-strip"
        className={tutorialCardClass(edge)}
        accessibilityLabel={`Tutorial help: ${copy.title}. ${copy.body}`}
        accessibilityHint="Long press for tip options"
        accessibilityActions={a11yActions}
        onAccessibilityAction={onA11yAction}
      >
        <MaterialCommunityIcons name="lightbulb-outline" size={20} color="#991b1b" />
        {/* One standard height for every tip (title + two body lines), so a
            one-line tip takes the same room as a two-line one and tall maps
            don't resize between phases. */}
        <View className="flex-1 gap-0.5 justify-center" style={{ minHeight: TIP_TEXT_MIN_HEIGHT }}>
          <Text className="font-stencil text-[11px] leading-[14px] uppercase text-blood">{copy.title}</Text>
          <Text className="font-typed text-xs leading-4 text-ink-soft">{copy.body}</Text>
        </View>
        {onToggleCollapsed && (
          <TouchableOpacity
            onPress={onToggleCollapsed}
            accessibilityLabel="Minimize tip"
            accessibilityRole="button"
            testID="tutorial-help-minimize"
            hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
            className="rounded-md border border-olive bg-paper items-center justify-center"
            style={{ width: 26, height: 26 }}
          >
            <MaterialCommunityIcons name="chevron-down" size={18} color="#991b1b" />
          </TouchableOpacity>
        )}
      </Pressable>
      {options}
    </>
  )
}

/** Long-press popup on a tip: turn tips off (back on from the game menu). */
function TutorialTipOptions({
  visible,
  onClose,
  onDisable,
}: {
  visible: boolean
  onClose: () => void
  onDisable: () => void
}) {
  return (
    <AppModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View className="flex-1 bg-black/60 items-center justify-center px-4">
        <Pressable accessible={false} onPress={onClose} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }} />
        <View testID="tutorial-tip-options" className="w-full bg-paper border-2 border-olive rounded-2xl p-5 gap-3" style={{ maxWidth: 360 }}>
          <Text className="font-stencil text-ink uppercase text-base tracking-widest">Inline tips</Text>
          <Text className="font-typed text-ink-soft text-sm">
            Turn tips off for now? You can turn them back on anytime from the game menu.
          </Text>
          <View className="flex-row justify-end gap-2">
            <TouchableOpacity
              onPress={onClose}
              accessibilityRole="button"
              testID="tutorial-tip-options-cancel"
              className="rounded-md border border-olive bg-paper px-3 py-2"
            >
              <Text className="font-stencil text-xs uppercase text-ink">Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onDisable}
              accessibilityRole="button"
              testID="tutorial-tip-options-disable"
              className="rounded-md bg-blood px-3 py-2"
            >
              <Text className="font-stencil text-xs uppercase text-white">Turn off tips</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </AppModal>
  )
}
